import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ClipboardList, FileText, Calendar, MessageSquare, Plus, Send, Loader2, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { format } from 'date-fns';

export default function BotInstructionsSection({ profile, isOwner }) {
  const queryClient = useQueryClient();
  const [showInstructionInput, setShowInstructionInput] = React.useState(false);
  const [instructionText, setInstructionText] = React.useState('');
  const [expandedSections, setExpandedSections] = React.useState({
    ownerInstructions: false,
    working: false,
    tasks: false,
    activities: false
  });
  const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  // Fetch current working task from BotMemory
  const { data: workingTask } = useQuery({
    queryKey: ['botMemory', 'WORKING_TASK', profile?.id],
    queryFn: async () => {
      const memories = await base44.entities.BotMemory.filter({
        bot_profile_id: profile.id,
        type: 'WORKING_TASK'
      }, '-date', 1);
      return memories[0] || null;
    },
    enabled: !!profile?.id
  });

  // Fetch active daily tasks assigned to this bot
  const { data: activeTasks } = useQuery({
    queryKey: ['botDailyTasks', profile?.user_email],
    queryFn: async () => {
      const today = format(new Date(), 'yyyy-MM-dd');
      const tasks = await base44.entities.DailyTask.filter({
        user_email: profile.user_email,
        date: today,
        completed: false,
        archived: false
      });
      return tasks || [];
    },
    enabled: !!profile?.user_email
  });

  // Fetch activities where bot is responsible
  const { data: activities } = useQuery({
    queryKey: ['botActivities', profile?.user_email],
    queryFn: async () => {
      const acts = await base44.entities.Activity.filter({
        responsible_email: profile.user_email,
        status: 'active'
      });
      return acts || [];
    },
    enabled: !!profile?.user_email
  });

  // Fetch owner instructions (manually created OWNER_INSTRUCTION type)
  const { data: ownerInstructions } = useQuery({
    queryKey: ['ownerInstructions', profile?.id],
    queryFn: async () => {
      const memories = await base44.entities.BotMemory.filter({
        bot_profile_id: profile.id,
        type: 'OWNER_INSTRUCTION'
      }, '-date', 20);
      return memories || [];
    },
    enabled: !!profile?.id
  });

  // Mutation for creating manual owner instruction
  const addInstructionMutation = useMutation({
    mutationFn: async (content) => {
      const today = new Date().toISOString().split('T')[0];
      return base44.entities.BotMemory.create({
        bot_profile_id: profile.id,
        type: 'OWNER_INSTRUCTION',
        date: today,
        content: content,
        owner_review_status: 'reviewed'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ownerInstructions'] });
      queryClient.invalidateQueries({ queryKey: ['botMemory'] });
      setInstructionText('');
      setShowInstructionInput(false);
    }
  });

  // Mutation for deleting an owner instruction
  const deleteInstructionMutation = useMutation({
    mutationFn: async (id) => {
      return base44.entities.BotMemory.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ownerInstructions'] });
      queryClient.invalidateQueries({ queryKey: ['botMemory'] });
    }
  });

  return (
    <Card className="glass-panel border-none">
      <CardHeader>
        <CardTitle className="text-slate-700 font-semibold tracking-tight leading-none flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-emerald-400" />
          Instructions for Bot
        </CardTitle>
        <p className="text-xs text-slate-500 mt-2">Current tasks, activities, and owner directives for this AI Officer</p>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* Owner Instructions — always show for bot owner */}
        {isOwner && (
          <Collapsible open={expandedSections.ownerInstructions} onOpenChange={() => toggleSection('ownerInstructions')}>
            <CollapsibleTrigger className="w-full">
              <div className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
                <div className="flex items-center gap-2">
                  {expandedSections.ownerInstructions ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                  <MessageSquare className="w-4 h-4 text-indigo-400" />
                  <h4 className="text-slate-700 text-sm font-semibold uppercase tracking-wider">Owner Instructions</h4>
                  <Badge variant="outline" className="text-xs border-indigo-500/30 text-indigo-500">
                    {ownerInstructions?.length || 0} active
                  </Badge>
                </div>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="space-y-2 mt-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-500">
                    Direct guidance the bot reads at the start of every conversation. Use this to correct behavior, set preferences, or teach patterns.
                  </p>
                  {!showInstructionInput && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowInstructionInput(true)}
                      className="border-indigo-500/30 text-indigo-500 hover:bg-indigo-500/10 h-7 text-xs"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Instruction
                    </Button>
                  )}
                </div>

                {/* Add instruction input */}
                {showInstructionInput && (
                  <div className="bg-slate-100 p-3 rounded-lg border border-indigo-500/30 space-y-2">
                    <Textarea data-testid="profile-botinstructionssection-textarea"
                      value={instructionText}
                      onChange={(e) => setInstructionText(e.target.value)}
                      placeholder="E.g., 'When users ask about sales, always default to the orders table unless they specify transactions.' or 'Always exclude cancelled orders unless explicitly asked to include them.'"
                      className="bg-white border-slate-300 text-slate-800 min-h-[80px] text-sm"
                    />
                    <div className="flex gap-2 justify-end">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setShowInstructionInput(false); setInstructionText(''); }}
                        className="h-7 text-xs text-slate-500"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => instructionText.trim() && addInstructionMutation.mutate(instructionText.trim())}
                        disabled={!instructionText.trim() || addInstructionMutation.isPending}
                        className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700"
                      >
                        {addInstructionMutation.isPending ? (
                          <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Saving...</>
                        ) : (
                          <><Send className="w-3 h-3 mr-1" />Save Instruction</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* List of existing instructions */}
                {ownerInstructions?.length > 0 ? (
                  <div className="space-y-2">
                    {ownerInstructions.map((instruction) => (
                      <div key={instruction.id} className="bg-slate-100 p-3 rounded-lg border border-slate-200 group">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-700 break-words whitespace-pre-wrap overflow-wrap-anywhere">{instruction.content}</p>
                            <p className="text-xs text-slate-400 mt-1">
                              Added {format(new Date(instruction.date), 'MMM d, yyyy')}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              if (window.confirm('Remove this instruction?')) {
                                deleteInstructionMutation.mutate(instruction.id);
                              }
                            }}
                            className="h-6 w-6 p-0 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-rose-400"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-slate-50 p-3 rounded-lg border border-dashed border-slate-300 text-center text-slate-400 text-sm">
                    No owner instructions yet. Instructions you add here will guide the bot's behavior in every conversation.
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Current Working Task from Memory — always show */}
        <Collapsible open={expandedSections.working} onOpenChange={() => toggleSection('working')}>
          <CollapsibleTrigger className="w-full">
            <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
              {expandedSections.working ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
              <FileText className="w-4 h-4 text-amber-400" />
              <h4 className="text-slate-700 text-sm font-semibold uppercase tracking-wider">WORKING.md (Current Task)</h4>
              {workingTask && (
                <Badge variant="outline" className="text-xs border-amber-500/30 text-amber-400">
                  {format(new Date(workingTask.date), 'MMM d, yyyy')}
                </Badge>
              )}
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            {workingTask ? (
              <div className="bg-slate-100 p-4 rounded-lg border border-slate-800 prose prose-sm prose-slate max-w-none break-words overflow-hidden mt-2">
                <ReactMarkdown>{workingTask.content}</ReactMarkdown>
              </div>
            ) : (
              <div className="bg-slate-50 p-4 rounded-lg border border-dashed border-slate-300 text-center text-slate-400 text-sm mt-2">
                No current working task. Tasks will appear here when the bot works on something.
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>

        {/* Active Daily Tasks */}
        <Collapsible open={expandedSections.tasks} onOpenChange={() => toggleSection('tasks')}>
          <CollapsibleTrigger className="w-full">
            <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
              {expandedSections.tasks ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
              <Calendar className="w-4 h-4 text-cyan-400" />
              <h4 className="text-slate-700 text-sm font-semibold uppercase tracking-wider">Today's Tasks</h4>
              <Badge variant="outline" className="text-xs border-cyan-500/30 text-cyan-400">
                {activeTasks?.length || 0} active
              </Badge>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2">
              {activeTasks?.length > 0 ? (
                <div className="space-y-2">
                  {activeTasks.map((task) => (
                    <div key={task.id} className="bg-slate-100 p-3 rounded-lg border border-slate-800">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-slate-700 text-sm break-words whitespace-pre-wrap">{task.task_description}</p>
                          {task.key_result_focus && (
                            <p className="text-xs text-slate-500 mt-1 break-words">KR Focus: {task.key_result_focus}</p>
                          )}
                        </div>
                        <Badge 
                          variant="outline" 
                          className={`text-xs ml-2 flex-shrink-0 ${
                            task.status_flag === 'green' ? 'border-emerald-500/30 text-emerald-400' :
                            task.status_flag === 'yellow' ? 'border-amber-500/30 text-amber-400' :
                            task.status_flag === 'red' ? 'border-rose-500/30 text-rose-400' :
                            'border-slate-500/30 text-slate-400'
                          }`}
                        >
                          {task.status_flag || 'gray'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-slate-100 p-4 rounded-lg border border-slate-800 text-center text-slate-500 text-sm">
                  No active tasks for today
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Active Activities */}
        <Collapsible open={expandedSections.activities} onOpenChange={() => toggleSection('activities')}>
          <CollapsibleTrigger className="w-full">
            <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
              {expandedSections.activities ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
              <ClipboardList className="w-4 h-4 text-purple-400" />
              <h4 className="text-slate-700 text-sm font-semibold uppercase tracking-wider">Assigned Activities</h4>
              <Badge variant="outline" className="text-xs border-purple-500/30 text-purple-400">
                {activities?.length || 0} active
              </Badge>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2">
              {activities?.length > 0 ? (
                <div className="space-y-2">
                  {activities.map((activity) => (
                    <div key={activity.id} className="bg-slate-100 p-3 rounded-lg border border-slate-800">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-slate-700 text-sm font-medium break-words">{activity.title}</p>
                          {activity.description && (
                            <p className="text-xs text-slate-500 mt-1 break-words">{activity.description}</p>
                          )}
                          {activity.activity_code && (
                            <Badge variant="outline" className="text-xs mt-2 border-slate-600 text-slate-400">
                              {activity.activity_code}
                            </Badge>
                          )}
                        </div>
                        <Badge 
                          variant="outline" 
                          className={`text-xs ml-2 flex-shrink-0 ${
                            activity.status_flag === 'green' ? 'border-emerald-500/30 text-emerald-400' :
                            activity.status_flag === 'yellow' ? 'border-amber-500/30 text-amber-400' :
                            'border-rose-500/30 text-rose-400'
                          }`}
                        >
                          {activity.status_flag || 'yellow'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-slate-100 p-4 rounded-lg border border-slate-800 text-center text-slate-500 text-sm">
                  No active activities assigned
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}